/**
 * Typed client for the admin API.
 *
 * Same-origin on purpose: the session cookie is HttpOnly and SameSite=Lax, so there is
 * no token to hold and nothing to put in localStorage. In development Vite proxies
 * /admin to Nest for the same reason — a cross-origin call would simply not carry it.
 */

// A merchant's logins are all customer_admin, locked to their own organization.
export type Role = 'staff' | 'customer_admin';
export type ProviderCode = 'bml' | 'swipe';
export type Environment = 'prod' | 'uat';

/** Soft delete: nothing is removed, so every list can be asked for archived rows too. */
export type Status = 'active' | 'archived';

export interface SessionUser {
  id: string;
  email: string;
  role: Role;
  orgId: string | null;
}

/**
 * What sign-in and /auth/me answer. `mustChangePassword` means the login signed in with a
 * temporary password: until it chooses its own, the server refuses everything else.
 */
export type SignedInUser = SessionUser & { mustChangePassword: boolean };

/** A reset's temporary password: shown once, working until `expiresAt`. */
export interface TemporaryPassword {
  temporaryPassword: string;
  expiresAt: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status: Status;
  createdAt: string;
  _count?: { outlets: number; users: number };
}

export interface Outlet {
  id: string;
  orgId: string;
  name: string;
  mposShopId: string | null;
  timezone: string;
  status: Status;
  createdAt: string;
  _count?: { counters: number };
}

/** An outlet with the organization above it, so a breadcrumb can name both. */
export interface OutletDetail extends Outlet {
  org: { id: string; name: string; status: Status };
}

export interface CounterKey {
  id?: string;
  keyId: string;
  label?: string | null;
  createdAt?: string;
  revokedAt?: string | null;
}

export interface Counter {
  id: string;
  outletId: string;
  name: string;
  mposCounterId: string | null;
  status: Status;
  apiKeys?: CounterKey[];

  /** From the companion's last relay handshake: "0.9.0+abc1234", or null before its first. */
  companionVersion: string | null;
  lastSeenAt: string | null;
}

export interface ProviderConfig {
  id: string;
  orgId: string;
  outletId: string | null;
  providerCode: ProviderCode;
  enabled: boolean;
  environment: Environment;
  enabledMethods: string[] | null;
  hasCredentials: boolean;
  webhookUrl: string | null;
  status: Status;
  updatedAt: string;
}

/** What BML's /public/me says about the merchant behind a stored key. */
export interface MerchantProfile {
  merchantId: string;
  tradingName: string;
  registeredName: string;
  companyNumber: string;
  currencies: string[];
  reviewStatus: string;
  providers: {
    value: string;
    description: string;
    status: 'supported' | 'card' | 'unknown';
    displayName?: string;
  }[];
}

export interface User {
  id: string;
  email: string;
  role: Role;
  orgId: string | null;
  status: Status;
  lastLoginAt: string | null;
}

/**
 * Search hits, grouped by kind rather than merged: the two are opened at different
 * routes, and a flat list would need a badge on every row to say which was which.
 * Archived rows are included, so every hit carries its status.
 */
export interface SearchResults {
  organizations: { id: string; name: string; slug: string; status: Status }[];
  outlets: {
    id: string;
    name: string;
    status: Status;
    mposShopId: string | null;
    org: { id: string; name: string };
  }[];
}

/** Carries the server's message, which is written for the person reading it. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }

  /** Not signed in, or the session expired under them. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/admin${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError('Could not reach the server', 0);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? safeJson(text) : null;

  if (!response.ok) {
    // Nest puts validation failures in an array; show them all rather than the first.
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : (body?.message ?? `Request failed (${response.status})`);
    throw new ApiError(message, response.status);
  }

  return body as T;
}

function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

/** The kinds of thing the audit log records changes to. */
export type AuditEntity =
  | 'organization'
  | 'outlet'
  | 'counter'
  | 'user'
  | 'provider_config'
  | 'webhook_failure';

/** One recorded configuration change. See docs/protocol.md, "Audit log". */
export interface AuditEntry {
  id: string;
  at: string;
  action: string;
  entity: string;
  entityId: string;
  orgId: string | null;
  orgName: string | null;
  /** Null when no login acted - a counter claiming its own pairing code. */
  actor: { id: string; email: string; role: string } | null;
  detail: Record<string, unknown> | null;
}

export interface AuditPage {
  entries: AuditEntry[];
  /** Pass back as `before` for the next, older page; null when there is none. */
  nextBefore: string | null;
}

/**
 * A webhook that reached a real transaction or merchant and could not be used. See
 * docs/protocol.md, "Webhook alerts".
 */
export interface WebhookFailure {
  id: string;
  receivedAt: string;
  providerCode: ProviderCode;
  kind: 'signature' | 'unknown_transaction' | 'error' | string;
  /** Which check failed or what threw - written by the server for the person reading. */
  reason: string;
  org: { id: string; name: string } | null;
  outlet: { id: string; name: string } | null;
  counter: { id: string; name: string } | null;
  cloudTxnId: string | null;
  /**
   * The transaction as the cloud holds it now, while it still does. CONFIRMED means the
   * counter learned of the payment anyway; anything else is worth a look at the bank.
   */
  transaction: { amountLaari: string; currency: string; state: string } | null;
  acknowledgedAt: string | null;
  acknowledgedBy: { id: string; email: string } | null;
}

export interface WebhookFailureSummary {
  unacknowledged: number;
  /** When the oldest unacknowledged failure arrived. */
  since: string | null;
  latest: WebhookFailure | null;
}

export interface WebhookFailurePage {
  entries: WebhookFailure[];
  nextBefore: string | null;
}

/** Builds "?a=1&b=2", dropping anything unset so callers can pass optionals freely. */
function query(params: Record<string, string | boolean | undefined>): string {
  const pairs = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== false && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);
  return pairs.length ? `?${pairs.join('&')}` : '';
}

export const api = {
  auth: {
    me: () => request<SignedInUser>('/auth/me'),
    login: (email: string, password: string) => post<SignedInUser>('/auth/login', { email, password }),
    logout: () => post<void>('/auth/logout'),
    /** Your own password. A wrong current password is a 400, so it never signs you out. */
    changePassword: (currentPassword: string, newPassword: string) =>
      post<void>('/auth/password', { currentPassword, newPassword }),
  },

  /** Across organizations and outlets at once, scoped to what the caller may see. */
  search: (q: string) => request<SearchResults>(`/search${query({ q })}`),

  organizations: {
    list: (archived?: boolean) => request<Organization[]>(`/organizations${query({ archived })}`),
    get: (orgId: string) => request<Organization>(`/organizations/${orgId}`),
    create: (name: string, slug: string) => post<Organization>('/organizations', { name, slug }),
    archive: (orgId: string) => del<{ id: string; status: Status }>(`/organizations/${orgId}`),
    restore: (orgId: string) =>
      post<{ id: string; status: Status }>(`/organizations/${orgId}/restore`),
  },

  outlets: {
    list: (orgId?: string, archived?: boolean) =>
      request<Outlet[]>(`/outlets${query({ orgId, archived })}`),
    get: (outletId: string) => request<OutletDetail>(`/outlets/${outletId}`),
    create: (input: { orgId: string; name: string; mposShopId?: string; timezone?: string }) =>
      post<Outlet>('/outlets', input),
    update: (outletId: string, input: { name?: string; mposShopId?: string; timezone?: string }) =>
      patch<Outlet>(`/outlets/${outletId}`, input),
    archive: (outletId: string) => del<{ id: string; status: Status }>(`/outlets/${outletId}`),
    restore: (outletId: string) =>
      post<{ id: string; status: Status }>(`/outlets/${outletId}/restore`),
  },

  counters: {
    list: (outletId: string, archived?: boolean) =>
      request<Counter[]>(`/outlets/${outletId}/counters${query({ archived })}`),
    create: (input: { outletId: string; name: string; mposCounterId?: string }) =>
      post<Counter>('/counters', input),
    update: (counterId: string, input: { name?: string; mposCounterId?: string }) =>
      patch<Counter>(`/counters/${counterId}`, input),
    archive: (counterId: string) => del<{ id: string; status: Status }>(`/counters/${counterId}`),
    restore: (counterId: string) =>
      post<{ id: string; status: Status }>(`/counters/${counterId}/restore`),
    pairingCode: (counterId: string) =>
      post<{ code: string; expiresAt: string }>(`/counters/${counterId}/pairing-code`),
    issueKey: (counterId: string, label?: string) =>
      post<{ apiKey: string; keyId: string }>(`/counters/${counterId}/keys`, { label }),
    revokeKey: (counterId: string, keyId: string) =>
      del<void>(`/counters/${counterId}/keys/${keyId}`),
  },

  users: {
    list: (orgId?: string, archived?: boolean) =>
      request<User[]>(`/users${query({ orgId, archived })}`),
    /** Sorting Company logins, which belong to no organization. Staff only. */
    listStaff: (archived?: boolean) => request<User[]>(`/staff-users${query({ archived })}`),
    create: (input: { email: string; password: string; role: Role; orgId?: string }) =>
      post<User>('/users', input),
    archive: (userId: string) => del<{ id: string; status: Status }>(`/users/${userId}`),
    restore: (userId: string) => post<{ id: string; status: Status }>(`/users/${userId}/restore`),
    /** Signs the login out everywhere; it must choose a new password at its next sign-in. */
    resetPassword: (userId: string) => post<TemporaryPassword>(`/users/${userId}/password-reset`),
  },

  audit: {
    list: (filter: { orgId?: string; entity?: AuditEntity; before?: string }) =>
      request<AuditPage>(`/audit${query(filter)}`),
  },

  /** Staff only. */
  webhookFailures: {
    summary: () => request<WebhookFailureSummary>('/webhook-failures/summary'),
    list: (before?: string) => request<WebhookFailurePage>(`/webhook-failures${query({ before })}`),
    /** Marks everything up to `upTo` as seen; anything newer stays unread. */
    acknowledge: (upTo: string) =>
      post<{ acknowledged: number }>('/webhook-failures/acknowledge', { upTo }),
  },

  providerConfigs: {
    list: (orgId?: string, archived?: boolean) =>
      request<ProviderConfig[]>(`/provider-configs${query({ orgId, archived })}`),
    upsert: (input: {
      orgId: string;
      outletId?: string;
      providerCode: ProviderCode;
      enabled: boolean;
      environment: Environment;
      credentials?: Record<string, string>;
    }) => post<ProviderConfig>('/provider-configs', input),
    merchant: (id: string) => request<MerchantProfile>(`/provider-configs/${id}/merchant`),
    archive: (id: string) => del<{ id: string; status: Status }>(`/provider-configs/${id}`),
    restore: (id: string) => post<{ id: string; status: Status }>(`/provider-configs/${id}/restore`),
  },
};
